import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import Select from 'react-select'
import AsyncSelect from 'react-select/lib/Async';
import { withStyles } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import NoSsr from '@material-ui/core/NoSsr'
import TextField from '@material-ui/core/TextField'
import FormControl from '@material-ui/core/FormControl'
import FormHelperText from '@material-ui/core/FormHelperText'
import Paper from '@material-ui/core/Paper'
import Chip from '@material-ui/core/Chip'
import MenuItem from '@material-ui/core/MenuItem'
import CancelIcon from '@material-ui/icons/Cancel'
import { emphasize } from '@material-ui/core/styles/colorManipulator'

import dataProvider from '../../utils/dataProvider'
import { GET_LIST } from 'react-admin'

import { find, filter } from 'lodash'


const styles = theme => ({
  root: {
    flexGrow: 1,
    marginTop : 20,
  },
  label : {
    color : '#3CA7AC',
    fontSize : 18,
    fontWeight: 600,
    textTransform: 'capitalize',
  },
  inputWrapper : {
    '&:before' : {
        borderBottom: '1px solid #C7C7C7',
        '&::hover' : {
            borderBottom: '1px solid red',
        }
    },
  },
  input: {
    display: 'flex',
    padding: 0,
    border: '1px solid #C7C7C7',
    paddingLeft: 5,
    color: '#676767',
    marginTop: 6,
    backgroundColor : '#fff',
  },
  inputDisabled : {
    color : '#9E9E9E',
    backgroundColor : '#FCFCFC'
  },
  valueContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    flex: 1,
    alignItems: 'center',
    overflow: 'hidden',
  },
  chip: {
    margin: `${theme.spacing.unit / 2}px ${theme.spacing.unit / 4}px`,
  },
  chipFocused: {
    backgroundColor: emphasize(
      theme.palette.type === 'light' ? theme.palette.grey[300] : theme.palette.grey[700],
      0.08,
    ),
  },
  noOptionsMessage: {
    padding: `${theme.spacing.unit}px ${theme.spacing.unit * 2}px`,
  },
  singleValue: {
    fontSize: 16,
    color: '#676767',
    '&.disabled' : { color : '#9E9E9E' }
  },
  placeholder: {
    position: 'absolute',
    left: 2,
    fontSize: 16,
  },
  paper: {
    position: 'absolute',
    zIndex: 1,
    marginTop: theme.spacing.unit,
    left: 0,
    right: 0,
  },
  divider: {
    height: theme.spacing.unit * 2,
  },
})

function NoOptionsMessage(props) {
  return (
    <Typography
      color="textSecondary"
      className={props.selectProps.classes.noOptionsMessage}
      {...props.innerProps}
    >
      {props.children}
    </Typography>
  )
}

function inputComponent({ inputRef, ...props }) {
  return <div ref={inputRef} {...props} />
}

function Control(props) {
  return (
    <TextField
      fullWidth
      InputProps={{
        className : props.selectProps.classes.inputWrapper,
        inputComponent,
        inputProps: {
          className: classNames(
              props.selectProps.classes.input,
              props.selectProps.isDisabled ? props.selectProps.classes.inputDisabled : {}),
          inputRef: props.innerRef,
          children: props.children,
          ...props.innerProps,
        },
      }}
    //   disabled={props.selectProps.isDisabled}
      {...props.selectProps.textFieldProps}
    />
  )
}

function Option(props) {
  return (
    <MenuItem
      buttonRef={props.innerRef}
      selected={props.isFocused}
      component="div"
      style={{
        fontWeight: props.isSelected ? 500 : 400
      }}
      {...props.innerProps}
    >
      {props.children}
    </MenuItem>
  )
}

function Placeholder(props) {
  return (
    <Typography
      color="textSecondary"
      className={props.selectProps.classes.placeholder}
      {...props.innerProps}
    >
      {props.children}
    </Typography>
  )
}

function SingleValue(props) {
  return (
    <Typography
        className={
            classNames(
                props.selectProps.isDisabled ? 'disabled' : '',
                props.selectProps.classes.singleValue
            )}
        {...props.innerProps}
    >
      {props.children}
    </Typography>
  )
}

function ValueContainer(props) {
  return <div className={props.selectProps.classes.valueContainer}>{props.children}</div>
}

function MultiValue(props) {
  return (
    <Chip
      tabIndex={-1}
      label={props.children}
      className={classNames(props.selectProps.classes.chip, {
        [props.selectProps.classes.chipFocused]: props.isFocused,
      })}
      onDelete={props.removeProps.onClick}
      deleteIcon={<CancelIcon {...props.removeProps} />}
    />
  )
}

function Menu(props) {
  return (
    <Paper square className={props.selectProps.classes.paper} {...props.innerProps}>
      {props.children}
    </Paper>
  )
}

const components = {
  Control,
  Menu,
  MultiValue,
  NoOptionsMessage,
  Option,
  Placeholder,
  SingleValue,
  ValueContainer,
}

class SearchSelect extends React.Component {

  state = {
    single: null,
    multi: null,
    hideValue: false,
  }

  handleChange = name => data => {
      this.setState({
          [name]: data,
          hideValue: false
      })
      // Send changes to redux-form Field
      if(name === 'single'){
        this.props.input.onChange(data ? data.value : "")
      }
      else if(name === 'multi'){
        this.props.input.onChange(data ? data.map(d => d.value) : [])
      }
  }

  hideValue = hide => () => {
      this.setState({ hideValue: hide })
  }

  getDefaultValue(){
      const { options, input } = this.props
      if (options.length > 0 && input.value ){
          const value = input.value
          const valueType = typeof value
          if (valueType === "number" || valueType === "string"){
              return find(options, { value : value })
          }
          else if(valueType === "object" && Array.isArray(value)){
              return filter(options, (op) => value.indexOf(op.value) !== -1)
          }else {
              return ""
          }
      } else {
          return ""
      }
  }

  async getOptions(inputValue, resource) {
    if (!inputValue) {
      return [];
    }

    const { filters = {} } = this.props

    const params = {
        pagination: {
            page: 1,
            perPage: 10
        },
        sort: {
            field: 'name',
            order: 'ASC'
        },
        filter: { search : inputValue, ...filters }
    }

    const response = await dataProvider(GET_LIST, resource, params)

    return response.data.map(res => ({ value: res.id, label: res.name }))

  }

  render() {
    const {
        classes,
        theme,
        id,
        label,
        input,
        options,
        placeholder,
        resource,
        searchType,
        meta: { touched, invalid, error },
        clearOnClick,
        ...rest
    } = this.props

    const selectStyles = {
      input: base => ({
        ...base,
        color: theme.palette.text.primary,
        '& input': {
          font: 'inherit',
        },
      }),
    }

    const selectType = this.props.isMulti ? 'multi' : 'single'
    const hasErrors = (touched && invalid && error)

    if (!!clearOnClick) {
      rest.onFocus = this.hideValue(true)
      rest.onBlur = this.hideValue(false)
      rest.controlShouldRenderValue = !this.state.hideValue
    }

    return (
      <FormControl error={hasErrors} fullWidth>
        <NoSsr>
            {searchType === "async" ?
            <AsyncSelect
                cacheOptions
                classes={classes}
                styles={selectStyles}
                value={this.state[selectType] || this.getDefaultValue()}
                defaultOptions={options}
                loadOptions={(e) => this.getOptions(e, resource)}
                onChange={this.handleChange(selectType)}
                textFieldProps={{
                    label: label,
                    error : hasErrors,
                    InputLabelProps: {
                      shrink: true,
                      classes : {root : classes.label}
                    },
                }}
                components={components}
                placeholder={placeholder}
                {...rest}
            />
            : <Select
                classes={classes}
                styles={selectStyles}
                name={input.name}
                value={this.state[selectType] || this.getDefaultValue()}
                options={options}
                onChange={this.handleChange(selectType)}
                textFieldProps={{
                    label: label,
                    error : hasErrors,
                    InputLabelProps: {
                      shrink: true,
                      classes : {root : classes.label}
                    },
                }}
                components={components}
                placeholder={placeholder}
                isClearable
                {...rest}
            />}
            {(touched && invalid && error) &&
            <FormHelperText>{error}</FormHelperText>}
          </NoSsr>
      </FormControl>
    )
  }
}

SearchSelect.propTypes = {
  classes: PropTypes.object.isRequired,
  theme: PropTypes.object.isRequired,
}

export default withStyles(styles, { withTheme: true })(SearchSelect)
